import type { FC, MouseEvent } from "react";

import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronRightIcon, PresentPlayIcon } from "mds/icons";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { FeatureStatus, featurePage } from "utils/presentation";

export type FeatureBreadcrumbsProps = {
  featureStatus: FeatureStatus | null;
  classSessionId: string;
  featureBlockId?: string;
  showSingleQuestionStep: boolean;
  pageId: string;
};

export const FeatureBreadcrumbs: FC<FeatureBreadcrumbsProps> = ({
  featureStatus,
  classSessionId,
  featureBlockId,
  showSingleQuestionStep,
  pageId,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "interaction_mode", "released_at"],
  );

  if (!page) {
    return null;
  }

  const showingSingleQuestion = featureStatus === FeatureStatus.CLASS_RESPONSES_WITH_BLOCK;
  const showingSingleResponse = featureStatus === FeatureStatus.PARTICIPANT_RESPONSE_WITH_BLOCK;

  const onShowAllQuestions = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    featurePage(classSessionId, page, {
      blockId: null,
      accessId: null,
    });
  };

  const onShowSingleQuestions = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    featurePage(classSessionId, page, {
      blockId: featureBlockId,
      accessId: null,
    });
  };

  return (
    <div className="flex items-center gap-1 text-black-tint-20">
      <PresentPlayIcon className="mr-1 text-green" />

      <SquareButton
        eventName="Feature Modal - Breadcrumbs"
        kind="ghost"
        size="m"
        onClick={onShowAllQuestions}
      >
        {t("presentation.featuring.class_responses")}
      </SquareButton>

      {(showSingleQuestionStep || showingSingleQuestion) && (
        <>
          <ChevronRightIcon />

          <SquareButton
            disabled={showingSingleQuestion}
            kind="ghost"
            size="m"
            onClick={onShowSingleQuestions}
          >
            {t("presentation.featuring.individual_questions")}
          </SquareButton>
        </>
      )}

      {showingSingleResponse && (
        <>
          <ChevronRightIcon />

          {t("presentation.featuring.status.participant_response_with_block")}
        </>
      )}
    </div>
  );
};
